import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react'
import { KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material'
import { Container, Track, Thumb, ButtonContainer, ArrowButton } from './styles'

type Props = {
  containerRef: React.RefObject<HTMLElement>
  className?: string
  style?: React.CSSProperties
  onVisibilityChange?: (isVisible: boolean) => void
}

const CustomScrollbar: React.FC<Props> = ({ containerRef, className, style, onVisibilityChange }) => {
  const [isVisible, setIsVisible] = useState(false)
  const [thumbHeight, setThumbHeight] = useState(0)
  const [thumbTop, setThumbTop] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [startY, setStartY] = useState(0)
  const [startScrollTop, setStartScrollTop] = useState(0)
  const trackRef = useRef<HTMLDivElement>(null)

  const updateScrollbar = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const { clientHeight, scrollHeight, scrollTop } = container
    const needsScroll = scrollHeight > clientHeight

    if (needsScroll !== isVisible) {
      setIsVisible(needsScroll)
      if (onVisibilityChange) {
        onVisibilityChange(needsScroll)
      }
    }

    if (!needsScroll) {
      setThumbHeight(0)
      return
    }

    const track = trackRef.current
    if (!track) return

    const trackHeight = track.clientHeight
    const scrollRatio = clientHeight / scrollHeight
    const newThumbHeight = Math.max(trackHeight * scrollRatio, 20) // Min height 20px for thumb
    const maxTop = trackHeight - newThumbHeight
    const scrollPercent = scrollTop / (scrollHeight - clientHeight)
    const newTop = scrollPercent * maxTop

    setThumbHeight(newThumbHeight)
    setThumbTop(newTop)
  }, [containerRef, isVisible, onVisibilityChange])

  useLayoutEffect(() => {
    if (isVisible) {
      updateScrollbar()
    }
  }, [isVisible, updateScrollbar])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      if (!isDragging) {
        updateScrollbar()
      }
    }

    // Observer for resize
    const resizeObserver = new ResizeObserver(() => {
      updateScrollbar()
    })

    container.addEventListener('scroll', handleScroll)
    resizeObserver.observe(container)
    // Also observe the first child (table) to detect content changes
    if (container.firstElementChild) {
      resizeObserver.observe(container.firstElementChild)
    }

    updateScrollbar()

    return () => {
      container.removeEventListener('scroll', handleScroll)
      resizeObserver.disconnect()
    }
  }, [containerRef, isDragging, updateScrollbar])

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
    setStartY(e.clientY)
    if (containerRef.current) {
      setStartScrollTop(containerRef.current.scrollTop)
    }
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current || !trackRef.current) return

      const deltaY = e.clientY - startY
      const container = containerRef.current
      const { clientHeight, scrollHeight } = container

      // Calculate how much to scroll based on thumb movement
      // Track height available for thumb movement = clientHeight - thumbHeight (approx, ignoring padding for now)
      // Actually track height is what matters.
      // But here Container is flex, Track is flex 1.
      // We need exact track height.

      const trackHeight = trackRef.current.clientHeight
      const scrollableHeight = scrollHeight - clientHeight
      const trackScrollableHeight = trackHeight - thumbHeight

      if (trackScrollableHeight <= 0) return

      const scrollDelta = (deltaY / trackScrollableHeight) * scrollableHeight

      container.scrollTop = startScrollTop + scrollDelta
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, startY, startScrollTop, thumbHeight, containerRef])

  const handleScrollButton = (direction: 'up' | 'down') => {
    if (!containerRef.current) return
    const scrollAmount = 40 // Step size
    containerRef.current.scrollBy({
      top: direction === 'up' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    })
  }

  // If no scroll needed, don't render? Or render empty track?
  // "width: 12px ... min-height: 80px". It seems it should always be visible.

  if (!isVisible) return null

  return (
    <Container className={className} style={style}>
      <Track
        ref={trackRef}
        onClick={() => {
          // Optional: Click track to jump
          // Implementation omitted for brevity/safety unless requested
        }}
      >
        {thumbHeight > 0 && <Thumb style={{ height: thumbHeight, top: thumbTop }} onMouseDown={handleMouseDown} />}
      </Track>
      <ButtonContainer>
        <ArrowButton onClick={() => handleScrollButton('up')} type="button">
          <KeyboardArrowUp />
        </ArrowButton>
        <ArrowButton onClick={() => handleScrollButton('down')} type="button">
          <KeyboardArrowDown />
        </ArrowButton>
      </ButtonContainer>
    </Container>
  )
}

export default CustomScrollbar
